(function() {
  'use strict';

  // ==================================================================
  // CONFIG
  // ==================================================================
  const CONFIG = {
    ROOT_DOMAIN: '.nosotrostequila.com',
    COOKIE_NAME: 'nosotros_shared_consent',
    SIGNAL_KEY: 'nosotros_shared_consent_signal',
    COOKIE_DAYS: 365,
    RECONSENT_DAYS: 365,

    // Consentmo's own cookies that drive the preferences modal toggles.
    // We mirror these to the root domain so both stores show the same
    // toggle state in the modal.
    CONSENTMO_STATUS_COOKIE: 'cookieconsent_status',
    CONSENTMO_DISABLED_COOKIE: 'cookieconsent_preferences_disabled',

    SHADOW_HOST_SELECTORS: [
      // Consentmo (current version) — primary targets
      '#csm-wrapper',
      '.csm-wrapper',
      '.csm-csb',
      '[data-csm-data-container]',
      // Legacy Consentmo / iSenseLabs naming
      'isense-cookie-consent',
      'isense-cookie-bar',
      '[id^="isense-cc"]',
      '[class*="isense-cc"]',
      'consentmo-app',
      'hu-app'
    ],

    // Clicking any element matching these selectors will UN-HIDE the
    // banner so the user can re-open preferences.
    REOPEN_LINK_SELECTORS: [
      '.csm-accessibility-link',
      '.csm-cookie-widget-link'
    ],

    DEBUG: true
  };

  // ==================================================================
  // STATE — tracked so we can stop hide watchers when user reopens
  // ==================================================================
  let preemptiveStyle = null;
  let hideWatcherInterval = null;
  let hideWatcherObserver = null;
  let bannerHideActive = false;
  // Set to true only when we receive a real user-driven consent event.
  // Prevents the fallback poll from writing Shopify's default state
  // to the cookie before the user has actually clicked anything.
  let userConsentReceived = false;
  // Track most recent user interaction (click/touch/key). Used to
  // distinguish real consent actions from auto-fired page-load events.
  let lastUserInteraction = 0;
  const USER_INTERACTION_WINDOW_MS = 3000;

  function recordInteraction() { lastUserInteraction = Date.now(); }
  document.addEventListener('click', recordInteraction, true);
  document.addEventListener('touchstart', recordInteraction, true);
  document.addEventListener('keydown', recordInteraction, true);

  function wasRecentUserInteraction() {
    return Date.now() - lastUserInteraction < USER_INTERACTION_WINDOW_MS;
  }

  // ==================================================================
  // STEP 1: PREEMPTIVE HIDE (runs immediately on script execution)
  // ==================================================================
  function quickCookieCheck() {
    try {
      const match = document.cookie.match(
        new RegExp('(?:^|; )' + CONFIG.COOKIE_NAME + '=([^;]*)')
      );
      if (!match) return false;
      const consent = JSON.parse(decodeURIComponent(match[1]));
      if (!consent || !consent.timestamp) return false;
      if (Date.now() - consent.timestamp > CONFIG.RECONSENT_DAYS * 864e5) return false;
      if (navigator.globalPrivacyControl === true) return false;
      return true;
    } catch (err) {
      return false;
    }
  }

  const shouldHidePreemptively = quickCookieCheck();

  if (shouldHidePreemptively) {
    preemptiveStyle = document.createElement('style');
    preemptiveStyle.id = 'consent-sync-preemptive-hide';
    preemptiveStyle.textContent =
      CONFIG.SHADOW_HOST_SELECTORS.join(', ') + ',' +
      '#csm-wrapper, .csm-wrapper, .csm-csb,' +
      '[data-csm-data-container],' +
      '[id*="cookie" i]:not(html):not(body):not(head):not(main):not(footer):not(header),' +
      '[class*="cookie-bar" i],' +
      '[class*="cookie-banner" i],' +
      '[class*="cookie-consent" i],' +
      '[class*="cookieconsent" i],' +
      '[class*="consent-banner" i],' +
      '[class*="consent-bar" i],' +
      '[id*="consent" i]:not(html):not(body):not(head):not(main):not(footer):not(header)' +
      ' { display: none !important; visibility: hidden !important; opacity: 0 !important; }';
    (document.head || document.documentElement).appendChild(preemptiveStyle);
    bannerHideActive = true;
  }

  // ==================================================================
  // INTERNAL
  // ==================================================================
  const log = CONFIG.DEBUG
    ? function() { console.log.apply(console, ['[ConsentSync]'].concat([].slice.call(arguments))); }
    : function() {};

  log('========================================');
  log('Init on:', location.hostname);
  log('Preemptive hide:', shouldHidePreemptively);
  log('========================================');

  function normalizeShopifyPayload(detail) {
    if (!detail || typeof detail !== 'object') return null;
    const legacyKeys = ['marketing', 'analytics', 'preferences', 'sale_of_data'];
    const newKeys = ['marketingAllowed', 'analyticsAllowed', 'preferencesAllowed', 'saleOfDataAllowed'];
    const hasLegacy = legacyKeys.some(k => detail[k] === 'yes' || detail[k] === 'no');
    const hasNew = newKeys.some(k => typeof detail[k] === 'boolean');
    if (!hasLegacy && !hasNew) return null;
    function read(legacyKey, newKey) {
      if (detail[legacyKey] === 'yes') return true;
      if (detail[legacyKey] === 'no') return false;
      if (typeof detail[newKey] === 'boolean') return detail[newKey];
      return null;
    }
    return {
      analytics: read('analytics', 'analyticsAllowed'),
      marketing: read('marketing', 'marketingAllowed'),
      preferences: read('preferences', 'preferencesAllowed'),
      sale_of_data: read('sale_of_data', 'saleOfDataAllowed')
    };
  }

  function mergeConsent(existing, incoming) {
    const base = existing || { analytics: false, marketing: false, preferences: false, sale_of_data: false };
    function pick(field) {
      return incoming[field] !== null && incoming[field] !== undefined
        ? incoming[field] : !!base[field];
    }
    return {
      analytics: pick('analytics'),
      marketing: pick('marketing'),
      preferences: pick('preferences'),
      sale_of_data: pick('sale_of_data')
    };
  }

  // ---------- Cookie helpers ----------
  function setSharedCookie(value) {
    try {
      const expires = new Date(Date.now() + CONFIG.COOKIE_DAYS * 864e5).toUTCString();
      const secure = location.protocol === 'https:' ? 'Secure; ' : '';
      document.cookie =
        CONFIG.COOKIE_NAME + '=' + encodeURIComponent(JSON.stringify(value)) + '; ' +
        'domain=' + CONFIG.ROOT_DOMAIN + '; ' +
        'path=/; expires=' + expires + '; SameSite=Lax; ' + secure;
      log('Cookie written:', value);
    } catch (err) { log('Cookie write failed:', err); }
  }

  function getSharedCookie() {
    try {
      const match = document.cookie.match(new RegExp('(?:^|; )' + CONFIG.COOKIE_NAME + '=([^;]*)'));
      if (!match) return null;
      return JSON.parse(decodeURIComponent(match[1]));
    } catch (err) { return null; }
  }

  function isStale(consent) {
    if (!consent || !consent.timestamp) return true;
    return Date.now() - consent.timestamp > CONFIG.RECONSENT_DAYS * 864e5;
  }

  function hasGPCSignal() { return navigator.globalPrivacyControl === true; }

  // ==================================================================
  // CONSENTMO STATE MIRRORING
  // ==================================================================
  // Consentmo uses two cookies to remember the user's modal toggle state:
  //   cookieconsent_status = "accept_all" | "accept_selected" | "reject_all"
  //   cookieconsent_preferences_disabled = comma-separated list of disabled
  //     categories, e.g. "marketing" or "analytics,marketing"
  //
  // Consentmo writes these scoped to its own subdomain only, so they
  // don't naturally share across stores. We mirror them to the root
  // domain so both stores' modals show consistent toggle state.

  // Categories Consentmo recognizes in its disabled list
  const CONSENTMO_CATEGORIES = {
    analytics: 'analytics',
    marketing: 'marketing',
    preferences: 'functional'  // Consentmo calls this "functional" in some versions
  };

  function setRootCookie(name, value) {
    try {
      const expires = new Date(Date.now() + CONFIG.COOKIE_DAYS * 864e5).toUTCString();
      const secure = location.protocol === 'https:' ? 'Secure; ' : '';
      document.cookie =
        name + '=' + encodeURIComponent(value) + '; ' +
        'domain=' + CONFIG.ROOT_DOMAIN + '; ' +
        'path=/; expires=' + expires + '; SameSite=Lax; ' + secure;
    } catch (err) { log('Root cookie write failed for ' + name + ':', err); }
  }

  function readCookie(name) {
    try {
      const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (err) { return null; }
  }

  // Translate our cookie state to Consentmo's disabled-categories format
  function buildConsentmoState(consent) {
    const disabled = [];
    if (!consent.analytics) disabled.push('analytics');
    if (!consent.marketing) disabled.push('marketing');
    if (!consent.preferences) disabled.push('functional');

    // Determine status string based on what's enabled
    let status;
    if (disabled.length === 0) {
      status = 'accept_all';
    } else if (disabled.length === 3) {
      status = 'reject_all';
    } else {
      status = 'accept_selected';
    }

    return { status: status, disabled: disabled.join(',') };
  }

  function mirrorConsentmoState(consent) {
    const state = buildConsentmoState(consent);
    setRootCookie(CONFIG.CONSENTMO_STATUS_COOKIE, state.status);
    setRootCookie(CONFIG.CONSENTMO_DISABLED_COOKIE, state.disabled);
    log('Mirrored Consentmo state:', state);
  }

  // Write Consentmo cookies BOTH to the root domain (so both stores see)
  // AND to the current subdomain explicitly (so Consentmo on THIS store
  // reads them during init, since Consentmo typically reads subdomain-
  // scoped cookies first).
  function setSubdomainCookie(name, value) {
    try {
      const expires = new Date(Date.now() + CONFIG.COOKIE_DAYS * 864e5).toUTCString();
      const secure = location.protocol === 'https:' ? 'Secure; ' : '';
      document.cookie =
        name + '=' + encodeURIComponent(value) + '; ' +
        'path=/; expires=' + expires + '; SameSite=Lax; ' + secure;
    } catch (err) {}
  }

  function mirrorConsentmoStateLocalAndRoot(consent) {
    const state = buildConsentmoState(consent);
    // Root-scoped (visible to all subdomains)
    setRootCookie(CONFIG.CONSENTMO_STATUS_COOKIE, state.status);
    setRootCookie(CONFIG.CONSENTMO_DISABLED_COOKIE, state.disabled);
    // Subdomain-scoped (read by Consentmo on this store)
    setSubdomainCookie(CONFIG.CONSENTMO_STATUS_COOKIE, state.status);
    setSubdomainCookie(CONFIG.CONSENTMO_DISABLED_COOKIE, state.disabled);
    log('Mirrored Consentmo state (local + root):', state);
  }

  // ==================================================================
  // SHADOW DOM HIDING
  // ==================================================================
  function findShadowBannerHosts() {
    const hosts = [];
    const all = document.querySelectorAll('*');
    for (let i = 0; i < all.length; i++) {
      const el = all[i];
      if (el.shadowRoot) {
        try {
          const shadowText = el.shadowRoot.textContent || '';
          if (/cookie|consent|do not sell|privacy preference/i.test(shadowText)) {
            hosts.push(el);
          }
        } catch (e) {}
      }
    }
    return hosts;
  }

  function hideShadowHosts() {
    if (!bannerHideActive) return 0;
    const shadowHosts = findShadowBannerHosts();
    shadowHosts.forEach(host => {
      host.style.setProperty('display', 'none', 'important');
    });
    return shadowHosts.length;
  }

  function startPersistentBannerHide() {
    bannerHideActive = true;
    hideShadowHosts();

    let attempts = 0;
    hideWatcherInterval = setInterval(() => {
      if (!bannerHideActive) {
        clearInterval(hideWatcherInterval);
        hideWatcherInterval = null;
        return;
      }
      attempts++;
      hideShadowHosts();
      if (attempts >= 50) {
        clearInterval(hideWatcherInterval);
        hideWatcherInterval = null;
      }
    }, 200);

    if (window.MutationObserver) {
      hideWatcherObserver = new MutationObserver(() => {
        if (!bannerHideActive) return;
        const newHosts = findShadowBannerHosts();
        newHosts.forEach(host => {
          if (host.style.display !== 'none') {
            host.style.setProperty('display', 'none', 'important');
          }
        });
      });
      hideWatcherObserver.observe(document.body || document.documentElement, {
        childList: true,
        subtree: true
      });
      setTimeout(() => {
        if (hideWatcherObserver) {
          hideWatcherObserver.disconnect();
          hideWatcherObserver = null;
        }
      }, 30000);
    }
  }

  // ==================================================================
  // SHOW BANNER — called when user clicks reopen link
  // ==================================================================
  function showBanner() {
    log('Show banner requested — removing all hide mechanisms');
    bannerHideActive = false;

    // 1. Remove preemptive CSS
    if (preemptiveStyle && preemptiveStyle.parentNode) {
      preemptiveStyle.parentNode.removeChild(preemptiveStyle);
      preemptiveStyle = null;
    }

    // 2. Stop the polling watcher
    if (hideWatcherInterval) {
      clearInterval(hideWatcherInterval);
      hideWatcherInterval = null;
    }

    // 3. Stop the mutation observer
    if (hideWatcherObserver) {
      hideWatcherObserver.disconnect();
      hideWatcherObserver = null;
    }

    // 4. Clear inline display:none from any shadow hosts we hid
    const hosts = findShadowBannerHosts();
    hosts.forEach(host => {
      host.style.removeProperty('display');
      host.style.removeProperty('visibility');
      host.style.removeProperty('opacity');
    });

    // 5. Also clear from any elements matching known selectors
    CONFIG.SHADOW_HOST_SELECTORS.forEach(sel => {
      try {
        document.querySelectorAll(sel).forEach(el => {
          el.style.removeProperty('display');
          el.style.removeProperty('visibility');
          el.style.removeProperty('opacity');
        });
      } catch (e) {}
    });

    log('Banner now visible');
  }

  // ==================================================================
  // CLICK HANDLER FOR REOPEN LINKS
  // ==================================================================
  // Use event delegation on document so it works even for links
  // that are added dynamically after page load.
  document.addEventListener('click', function(e) {
    const target = e.target;
    if (!target || !target.closest) return;

    for (let i = 0; i < CONFIG.REOPEN_LINK_SELECTORS.length; i++) {
      const sel = CONFIG.REOPEN_LINK_SELECTORS[i];
      if (target.closest(sel)) {
        log('Reopen link clicked:', sel);
        showBanner();
        return;
      }
    }
  }, true); // useCapture: catch the click before the page handles it

  // ==================================================================
  // SHOPIFY API
  // ==================================================================
  function waitForShopifyPrivacy(callback, attempts) {
    attempts = attempts || 0;
    if (window.Shopify && window.Shopify.customerPrivacy) return callback();
    if (attempts > 50) { log('Shopify.customerPrivacy never loaded'); return; }
    setTimeout(function() { waitForShopifyPrivacy(callback, attempts + 1); }, 100);
  }

  function applyConsent(consent) {
    if (!window.Shopify || !window.Shopify.customerPrivacy) return;
    let toApply = consent;
    if (hasGPCSignal()) {
      log('GPC detected — forcing opt-out');
      toApply = { analytics: false, marketing: false, preferences: false, sale_of_data: false };
    }
    const trackingConsent = {
      analytics: !!toApply.analytics,
      marketing: !!toApply.marketing,
      preferences: !!toApply.preferences,
      sale_of_data: !!toApply.sale_of_data
    };
    try {
      window.Shopify.customerPrivacy.setTrackingConsent(trackingConsent, function() {
        log('Consent applied to Shopify ✓', trackingConsent);
      });
    } catch (err) { log('setTrackingConsent failed:', err); }
  }

  function saveAndBroadcast(consent) {
    const payload = Object.assign({}, consent, { timestamp: Date.now() });
    setSharedCookie(payload);
    // Also mirror Consentmo's own state cookies to the root domain
    // so the preferences modal on the other store reflects the same toggles
    mirrorConsentmoState(consent);
    try { localStorage.setItem(CONFIG.SIGNAL_KEY, JSON.stringify(payload)); }
    catch (err) {}
  }

  function matchesExisting(incoming) {
    const existing = getSharedCookie();
    if (!existing) return false;
    return !!existing.analytics === !!incoming.analytics &&
           !!existing.marketing === !!incoming.marketing &&
           !!existing.preferences === !!incoming.preferences &&
           !!existing.sale_of_data === !!incoming.sale_of_data;
  }

  // ==================================================================
  // MAIN INIT
  // ==================================================================
  const existing = getSharedCookie();
  const haveValidConsent = existing && !isStale(existing);

  log('Existing cookie:', existing, '| valid:', haveValidConsent);

  if (haveValidConsent && !hasGPCSignal()) {
    // Mirror Consentmo's state cookies to the local subdomain so its
    // modal reads the correct toggle state when opened. Do this BEFORE
    // any banner-hide or Shopify-apply work so Consentmo sees the
    // cookies during its own init.
    mirrorConsentmoStateLocalAndRoot(existing);

    startPersistentBannerHide();
    waitForShopifyPrivacy(function() {
      applyConsent(existing);
      setTimeout(function() { applyConsent(existing); }, 1500);
    });
  } else {
    // No valid consent — remove preemptive CSS so banner can show
    if (preemptiveStyle && preemptiveStyle.parentNode) {
      preemptiveStyle.parentNode.removeChild(preemptiveStyle);
      preemptiveStyle = null;
    }
    bannerHideActive = false;
  }

  // ==================================================================
  // EVENT LISTENER — capture consent changes
  // ==================================================================
  document.addEventListener('visitorConsentCollected', function(e) {
    const detail = e.detail || {};
    log('visitorConsentCollected:', detail);
    const normalized = normalizeShopifyPayload(detail);
    if (!normalized) return;

    // CRITICAL: Only treat this as a real user consent action if it
    // fired shortly after a user interaction (click/touch/key). Some
    // CMPs auto-fire this on page load with default states — that's
    // NOT consent and we must not write the cookie in those cases.
    if (!wasRecentUserInteraction()) {
      log('Event fired without recent user interaction — ignoring (likely auto-fire)');
      return;
    }

    // This event represents a real user action — mark consent received
    // so the fallback poll is now allowed to write cookie updates.
    userConsentReceived = true;

    const existingCookie = getSharedCookie();
    const merged = mergeConsent(existingCookie, normalized);

    if (!matchesExisting(merged)) {
      saveAndBroadcast(merged);
    } else {
      log('Matches existing — skipping cookie update (loop prevention)');
    }

    // Re-hide the banner now that the user has finished interacting.
    // Small delay to let Consentmo finish its own close animation/state.
    if (!isStale(merged) && !hasGPCSignal()) {
      setTimeout(function() {
        log('Re-hiding banner after consent save');
        startPersistentBannerHide();

        // Reinstate the preemptive CSS too, in case Consentmo re-opens
        // the banner on next page load
        if (!preemptiveStyle || !preemptiveStyle.parentNode) {
          preemptiveStyle = document.createElement('style');
          preemptiveStyle.id = 'consent-sync-preemptive-hide';
          preemptiveStyle.textContent =
            CONFIG.SHADOW_HOST_SELECTORS.join(', ') + ',' +
            '#csm-wrapper, .csm-wrapper, .csm-csb,' +
            '[data-csm-data-container],' +
            '[id*="cookie" i]:not(html):not(body):not(head):not(main):not(footer):not(header),' +
            '[class*="cookie-bar" i],' +
            '[class*="cookie-banner" i],' +
            '[class*="cookie-consent" i],' +
            '[class*="cookieconsent" i],' +
            '[class*="consent-banner" i],' +
            '[class*="consent-bar" i],' +
            '[id*="consent" i]:not(html):not(body):not(head):not(main):not(footer):not(header)' +
            ' { display: none !important; visibility: hidden !important; opacity: 0 !important; }';
          (document.head || document.documentElement).appendChild(preemptiveStyle);
        }
      }, 500);
    }
  });

  // ==================================================================
  // FALLBACK POLL
  // ==================================================================
  // Only writes cookie updates AFTER a real user-driven event has fired.
  // This prevents Shopify's default consent state from being silently
  // captured and used to suppress the banner without user action.
  waitForShopifyPrivacy(function() {
    let lastState = null;
    setInterval(function() {
      // Skip until the user has actually interacted with the banner
      if (!userConsentReceived) return;
      try {
        const current = window.Shopify.customerPrivacy.currentVisitorConsent();
        const currentStr = JSON.stringify(current);
        if (!currentStr || currentStr === lastState) return;
        lastState = currentStr;
        const normalized = normalizeShopifyPayload(current);
        if (!normalized) return;
        if (normalized.analytics === null && normalized.marketing === null &&
            normalized.preferences === null && normalized.sale_of_data === null) return;
        const existingCookie = getSharedCookie();
        const merged = mergeConsent(existingCookie, normalized);
        if (matchesExisting(merged)) return;
        log('Fallback poll change:', current);
        saveAndBroadcast(merged);
      } catch (err) {}
    }, 1000);
  });

  // ==================================================================
  // CROSS-TAB SYNC
  // ==================================================================
  window.addEventListener('storage', function(e) {
    if (e.key !== CONFIG.SIGNAL_KEY || !e.newValue) return;
    try {
      const consent = JSON.parse(e.newValue);
      log('Cross-tab signal:', consent);
      setSharedCookie(consent);
      waitForShopifyPrivacy(function() { applyConsent(consent); });
      if (!isStale(consent) && !bannerHideActive) startPersistentBannerHide();
    } catch (err) {}
  });

  log('Consent sync initialized');
})();